import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '../../../../../icons/SvgIcons';
import { useParams } from 'react-router-dom';
import { getAISystemOverview } from '../../../../../services/AISystemsService';
import { updateEventChangeImpact } from '../../../../../services/SystemLoggingService';
import { SystemLoggingEvent } from '../../../../../types/SystemLogging.types';
import {
  formatEventDate,
  formatEventType,
  formatResource
} from '../../../../../utils/EventFormatting.util';
import { getEventUserDisplay } from '../../../../../utils/Functions.utils';
import {
  changeImpactOptions,
  changeImpactStyles,
  getChangeImpactLabel,
  normalizeChangeImpact,
  SupportedChangeImpact
} from '../../../../ui/ChangeImpactBadge/ChangeImpactBadge';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAppDispatch } from '../../../../../hooks/redux';
import {
  fetchCurrentOverviewFailure,
  fetchCurrentOverviewSuccess,
  setCurrentOverviewAiSystemId,
  setHasMajorSeverity
} from '../../../../../store/slices/aiSystemsSlice';

interface EventsTableRowProps {
  event: SystemLoggingEvent;
  onEventUpdated?: () => void;
  onEventImpactUpdated?: (
    eventId: string,
    impact: SupportedChangeImpact
  ) => void;
  canEditSeverity?: boolean;
  onRowClick?: (event: SystemLoggingEvent) => void;
}

export function EventsTableRow({
  event,
  onEventUpdated,
  onEventImpactUpdated,
  canEditSeverity = false,
  onRowClick
}: EventsTableRowProps) {
  const { id: aiSystemId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0 });
  const normalizedImpact = normalizeChangeImpact(event.changeImpact);
  const [selectedImpact, setSelectedImpact] =
    useState<SupportedChangeImpact>(normalizedImpact);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedImpact(normalizedImpact);
  }, [normalizedImpact]);

  const { name: userDisplayName, initials: userInitials } =
    getEventUserDisplay(event);
  const resourceLabel = event.repositoryName
    ? formatResource(event.repositoryName)
    : 'Repository';

  const handleImpactChange = async (impact: SupportedChangeImpact) => {
    setIsDropdownOpen(false);

    try {
      await updateEventChangeImpact(event.id, impact);
      setSelectedImpact(impact);
      onEventImpactUpdated?.(event.id, impact);

      if (aiSystemId) {
        try {
          const overview = await getAISystemOverview(aiSystemId);
          dispatch(setCurrentOverviewAiSystemId(aiSystemId));
          dispatch(fetchCurrentOverviewSuccess(overview));
          dispatch(setHasMajorSeverity(overview.repositories.hasMajor));
        } catch (error) {
          console.error('Failed to refresh overview data:', error);
          dispatch(
            fetchCurrentOverviewFailure('Failed to fetch overview data')
          );
        }
      }

      // Refresh trends chart after updating severity
      if (onEventUpdated) {
        onEventUpdated();
      }
    } catch (error) {
      console.error('Failed to update change impact:', error);
      alert('Failed to update change impact. Please try again.');
    }
  };

  const availableOptions = changeImpactOptions.filter(
    (option) => option !== selectedImpact
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !dropdownRef.current?.contains(event.target as Node) &&
        !dropdownMenuRef.current?.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    const handleScroll = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isDropdownOpen]);

  const handleSeverityDropdownClick = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();

    if (!canEditSeverity) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownHeight = availableOptions.length * 36 + 2;

    setDropdownPosition({
      left: rect.left,
      top:
        rect.bottom + dropdownHeight + 4 > window.innerHeight
          ? rect.top - dropdownHeight - 4
          : rect.bottom + 4
    });
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <tr
      onClick={() => onRowClick?.(event)}
      className="w-full flex flex-row cursor-pointer bg-white border-t border-gray-100 relative overflow-visible hover:bg-gray-50"
    >
      <td className="w-[22%] flex justify-between items-center py-3 px-6 gap-2 text-sm font-medium text-gray-900v2 text-left">
        {formatEventType(event.eventType)}
        <span>
          {event.changeImpact === 'MAJOR' && (
            <ExclamationTriangleIcon className="w-6 h-6 text-red-700" />
          )}
        </span>
      </td>

      <td className="w-[11%] flex py-3 px-6 gap-1 text-sm font-normal items-center text-gray-550 relative">
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={handleSeverityDropdownClick}
            disabled={!canEditSeverity}
            className={`inline-flex items-center gap-0.5 rounded-[10px] h-5 pt-0.5 pb-0.5 pl-2 pr-1 ${
              changeImpactStyles[selectedImpact].badge
            } ${changeImpactStyles[selectedImpact].hover} ${
              changeImpactStyles[selectedImpact].focus
            } ${!canEditSeverity ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            <span
              className={`font-inter font-medium text-xs leading-4 text-center ${changeImpactStyles[selectedImpact].text}`}
            >
              {getChangeImpactLabel(selectedImpact)}
            </span>
            <div className={changeImpactStyles[selectedImpact].stroke}>
              <ChevronDownIcon />
            </div>
          </button>

          {isDropdownOpen &&
            canEditSeverity &&
            createPortal(
              <div
                ref={dropdownMenuRef}
                className="fixed bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] min-w-[120px]"
                style={{
                  left: `${dropdownPosition.left}px`,
                  top: `${dropdownPosition.top}px`
                }}
              >
                {availableOptions.map((option) => (
                  <button
                    key={option}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImpactChange(option);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 font-inter text-sm text-gray-700 first:rounded-t-lg last:rounded-b-lg"
                  >
                    {option.charAt(0) + option.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>,
              document.body
            )}
        </div>
      </td>

      <td className="w-[18%] min-w-0 flex py-3 px-6 gap-1 text-sm font-normal items-center text-gray-550 relative">
        <span className="block flex-1 min-w-0 truncate text-left">
          {event.message}
        </span>
      </td>

      <td className="w-[20%] min-w-0 flex py-3 px-6 gap-1 text-sm font-normal items-center text-left text-gray-550 relative">
        <span className="block flex-1 min-w-0 truncate">{resourceLabel}</span>
      </td>

      <td className="w-[16%] flex py-3 px-6 gap-4 text-sm font-normal leading-5 items-center text-gray-550">
        <span className="w-6 h-6 rounded-xl bg-[#EEEEF0] flex items-center justify-center">
          <span className="font-inter text-center font-normal text-xs leading-3 text-[#4E4E56]">
            {userInitials}
          </span>
        </span>
        <span className="text-left">{userDisplayName}</span>
      </td>

      <td className="w-[13%] flex py-3 px-6 gap-1 text-sm font-normal leading-5 items-center text-gray-550 truncate relative">
        <span className="truncate">{formatEventDate(event.createdAt)}</span>
      </td>
    </tr>
  );
}
